// Modal para que el administrador complete o edite el perfil de un comprador
// Se abre desde ManageInvitesModal (modal sobre modal, z-index mayor)

import React, { useState, useEffect } from 'react';
import { X, CheckCircle, User, Home, Bath, Maximize2, TreePine, Wrench, Building, Car } from 'lucide-react';
import {
  PAYMENT_OPTIONS,
  FINANCING_OPTIONS,
  PROPERTY_TO_SELL_OPTIONS,
  OUTDOOR_OPTIONS,
  REFORM_OPTIONS,
  FEATURE_OPTIONS,
} from '../../config/buyerProfileOptions';
import {
  getBuyerProfile,
  createBuyerProfile,
  updateBuyerProfile,
} from '../../services/supabase/buyerProfiles';

const AdminBuyerProfileModal = ({ invite, property, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [existingProfileId, setExistingProfileId] = useState(null);

  // Campos del perfil
  const [paymentMethod, setPaymentMethod] = useState('');
  const [financingStatus, setFinancingStatus] = useState('');
  const [propertyToSell, setPropertyToSell] = useState('');
  const [minBedrooms, setMinBedrooms] = useState(null);
  const [minBathrooms, setMinBathrooms] = useState(null);
  const [desiredSqm, setDesiredSqm] = useState(null);
  const [outdoorSpace, setOutdoorSpace] = useState('');
  const [reformLevel, setReformLevel] = useState('');
  const [elevator, setElevator] = useState('');
  const [parking, setParking] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');

  const [fieldErrors, setFieldErrors] = useState({});

  // Cargar perfil existente si lo hay
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await getBuyerProfile(property.id, invite.token);
        if (profile) {
          setExistingProfileId(profile.id);
          setPaymentMethod(profile.paymentMethod || '');
          setFinancingStatus(profile.financingStatus || '');
          setPropertyToSell(profile.propertyToSell || '');
          setMinBedrooms(profile.minBedrooms || null);
          setMinBathrooms(profile.minBathrooms || null);
          setDesiredSqm(profile.desiredSqm || null);
          setOutdoorSpace(profile.outdoorSpace || '');
          setReformLevel(profile.reformLevel || '');
          setElevator(profile.elevator || '');
          setParking(profile.parking || '');
          setAdditionalComments(profile.additionalComments || '');
        }
      } catch (err) {
        console.error('Error cargando perfil:', err);
        setError('No se pudo cargar el perfil existente.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [property.id, invite.token]);

  const validate = () => {
    const errors = {};
    if (!paymentMethod) errors.paymentMethod = 'Obligatorio';
    if (!financingStatus) errors.financingStatus = 'Obligatorio';
    if (!propertyToSell) errors.propertyToSell = 'Obligatorio';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    setError('');

    const profileData = {
      paymentMethod,
      financingStatus,
      propertyToSell,
      minBedrooms,
      minBathrooms,
      desiredSqm,
      outdoorSpace: outdoorSpace || null,
      reformLevel: reformLevel || null,
      elevator: elevator || null,
      parking: parking || null,
      additionalComments: additionalComments || null,
    };

    try {
      if (existingProfileId) {
        await updateBuyerProfile(existingProfileId, profileData, {
          propertyId: property.id,
          inviteToken: invite.token,
        });
      } else {
        await createBuyerProfile({
          ...profileData,
          propertyId: property.id,
          inviteToken: invite.token,
          userEmail: invite.email,
        });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error guardando perfil:', err);
      setError('Error al guardar. Por favor, inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop — encima de ManageInvitesModal (z-50) */}
      <div
        className="fixed inset-0 bg-black/70 z-[80]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div
          className="bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-slate-700 flex-shrink-0">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <User className="w-5 h-5 text-amber-400" />
                <h3 className="text-xl font-bold text-white">
                  {existingProfileId ? 'Editar perfil' : 'Completar perfil'}
                </h3>
              </div>
              <p className="text-sm text-slate-400">
                <span className="text-white font-medium">{invite.name}</span>
                {' · '}
                <span>{invite.email}</span>
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="text-slate-400 hover:text-white transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Contenido */}
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
              </div>
            ) : (
              <>
                {/* Sección: Datos financieros */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    Situación financiera
                  </h4>
                  <div className="space-y-4">
                    {/* Método de pago */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">
                        Método de pago <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500 ${
                          fieldErrors.paymentMethod ? 'border-red-500' : 'border-slate-700'
                        }`}
                      >
                        <option value="">— Seleccionar —</option>
                        {PAYMENT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {fieldErrors.paymentMethod && (
                        <p className="text-xs text-red-400 mt-1">{fieldErrors.paymentMethod}</p>
                      )}
                    </div>

                    {/* Estado de financiación */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">
                        Financiación <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={financingStatus}
                        onChange={(e) => setFinancingStatus(e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500 ${
                          fieldErrors.financingStatus ? 'border-red-500' : 'border-slate-700'
                        }`}
                      >
                        <option value="">— Seleccionar —</option>
                        {FINANCING_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {fieldErrors.financingStatus && (
                        <p className="text-xs text-red-400 mt-1">{fieldErrors.financingStatus}</p>
                      )}
                    </div>

                    {/* Vivienda que vender */}
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-1.5">
                        ¿Tiene vivienda que vender? <span className="text-red-400">*</span>
                      </label>
                      <select
                        value={propertyToSell}
                        onChange={(e) => setPropertyToSell(e.target.value)}
                        className={`w-full bg-slate-900 border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500 ${
                          fieldErrors.propertyToSell ? 'border-red-500' : 'border-slate-700'
                        }`}
                      >
                        <option value="">— Seleccionar —</option>
                        {PROPERTY_TO_SELL_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {fieldErrors.propertyToSell && (
                        <p className="text-xs text-red-400 mt-1">{fieldErrors.propertyToSell}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sección: Preferencias de vivienda */}
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                    Preferencias de vivienda <span className="text-slate-600 font-normal normal-case">(opcional)</span>
                  </h4>
                  <div className="space-y-4">
                    {/* Habitaciones y baños */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                          <Home className="w-3.5 h-3.5 text-amber-400" />
                          Habitaciones mín.
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={minBedrooms || ''}
                          onChange={(e) => setMinBedrooms(e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="—"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                          <Bath className="w-3.5 h-3.5 text-amber-400" />
                          Baños mín.
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={minBathrooms || ''}
                          onChange={(e) => setMinBathrooms(e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="—"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                          <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
                          M² deseables
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={desiredSqm || ''}
                          onChange={(e) => setDesiredSqm(e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="—"
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>

                    {/* Zona exterior */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                        <TreePine className="w-3.5 h-3.5 text-amber-400" />
                        Zona exterior
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {OUTDOOR_OPTIONS.map((o) => (
                          <label
                            key={o.value}
                            className={`flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                              outdoorSpace === o.value
                                ? 'bg-amber-500/10 border-amber-500 text-white'
                                : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name="admin_outdoor_space"
                              value={o.value}
                              checked={outdoorSpace === o.value}
                              onChange={(e) => setOutdoorSpace(e.target.value)}
                              className="hidden"
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Reforma */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                        <Wrench className="w-3.5 h-3.5 text-amber-400" />
                        Reforma
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {REFORM_OPTIONS.map((o) => (
                          <label
                            key={o.value}
                            className={`flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                              reformLevel === o.value
                                ? 'bg-amber-500/10 border-amber-500 text-white'
                                : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                            }`}
                          >
                            <input
                              type="radio"
                              name="admin_reform_level"
                              value={o.value}
                              checked={reformLevel === o.value}
                              onChange={(e) => setReformLevel(e.target.value)}
                              className="hidden"
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Ascensor y Parking */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                          <Building className="w-3.5 h-3.5 text-amber-400" />
                          Ascensor
                        </label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {FEATURE_OPTIONS.map((o) => (
                            <label
                              key={o.value}
                              className={`flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                                elevator === o.value
                                  ? 'bg-amber-500/10 border-amber-500 text-white'
                                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                              }`}
                            >
                              <input
                                type="radio"
                                name="admin_elevator"
                                value={o.value}
                                checked={elevator === o.value}
                                onChange={(e) => setElevator(e.target.value)}
                                className="hidden"
                              />
                              {o.label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-300 mb-1.5">
                          <Car className="w-3.5 h-3.5 text-amber-400" />
                          Parking
                        </label>
                        <div className="grid grid-cols-3 gap-1.5">
                          {FEATURE_OPTIONS.map((o) => (
                            <label
                              key={o.value}
                              className={`flex items-center justify-center p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                                parking === o.value
                                  ? 'bg-amber-500/10 border-amber-500 text-white'
                                  : 'bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600'
                              }`}
                            >
                              <input
                                type="radio"
                                name="admin_parking"
                                value={o.value}
                                checked={parking === o.value}
                                onChange={(e) => setParking(e.target.value)}
                                className="hidden"
                              />
                              {o.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Comentarios */}
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        Comentarios adicionales
                      </label>
                      <textarea
                        value={additionalComments}
                        onChange={(e) => setAdditionalComments(e.target.value)}
                        placeholder="Cualquier detalle relevante comunicado por el comprador..."
                        rows={3}
                        maxLength={500}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-amber-500 resize-none"
                      />
                      <p className="text-xs text-slate-500 mt-1">{additionalComments.length}/500</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="px-5 py-3 bg-red-500/10 border-t border-red-500/20 flex-shrink-0">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 p-5 border-t border-slate-700 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={saving}
              className="flex-1 bg-slate-700 text-white font-medium py-2.5 rounded-lg hover:bg-slate-600 transition-all disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || loading}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold py-2.5 rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Guardando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {existingProfileId ? 'Actualizar perfil' : 'Guardar perfil'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminBuyerProfileModal;
